/**
 * The way back from a change that took something off the board, as a press on
 * the toast that announces it.
 *
 * ⌘Z is a route that does not exist on a phone, where Prune and Rewrite are
 * both fully reachable from the sheet, so the toast carries the step itself
 * (`Toast.action`); the keystroke keeps working beside it. One copy, because a
 * second one would be a second set of guards to forget.
 *
 * The action remembers WHICH history entry it restores, by identity: undo is a
 * stack, and with two such toasts on screen a tap on the older one would
 * silently pop the newer entry instead. A stale tap refuses and says why; once
 * the entries above it are undone, the same toast heals and works — the depth
 * check needs no bookkeeping beyond the reference itself.
 */

import { useStore, type Toast, type UndoEntry } from './store'

export function undoOffer(
  entry: UndoEntry | undefined,
  label: string,
): Toast['action'] | undefined {
  if (!entry) return undefined
  return {
    label,
    // `false` REFUSES: the renderer then leaves this toast standing, so a tap
    // that could not be honoured costs nothing. Dismissing first took the only
    // route back and the sentence explaining why in the same click.
    run: () => {
      const cur = useStore.getState()
      // The keystroke's own guard, carried over: undoing mid-pass would race the
      // drain still threading nodes on.
      if (cur.busy) {
        cur.toast('One thought at a time — let the current branch finish.', 'warn')
        return false
      }
      // The store's own undo door, tested here so the offer SURVIVES the
      // refusal: falling through to `undo()` would raise the same toast but
      // consume this one, and a press that could not be honoured must leave
      // the offer standing to be taken once the run settles.
      if (cur.run !== null && cur.run.phase !== 'ended') {
        cur.toast('One thought at a time — let the current branch finish.', 'warn')
        return false
      }
      if (cur.past[cur.past.length - 1] !== entry) {
        cur.toast('Newer changes sit on top — undo steps back in order.', 'info')
        return false
      }
      cur.undo()
    },
  }
}
